import { useMemo } from 'react';

import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { rolesEndpoints as endpoints } from '@/api/endpoints';
import { ApiError, callDelete, callGet, callPost, callPut } from '@/api/fetcher';
import { getPermissions, selectIsUserOwner } from '@/redux/permissions/selectors';
import { selectAccountId } from '@/redux/session/selectors';
import { OptionBase } from '@/shared/types/common';
import { actions } from '@/shared/types/permissions';
import { PartialRole, Role, RolePayload, Roles } from '@/shared/types/role';
import { RootState } from '@/shared/types/state';
import { BILLING, READER, WRITER } from '@/util/constants';
import { SYSTEM_ROLE_COPY } from '@/util/systemRoles';

type Option = OptionBase<{ type?: string; description?: string; disabled?: boolean }>;

const API = {
  listRoles: async (): Promise<Roles> => callGet(endpoints.roles),
  createRole: async (payload: RolePayload): Promise<Role> => callPost(endpoints.roles, payload),
  updateRole: async ({ role_id, ...payload }: RolePayload & { role_id: string }): Promise<Role> =>
    callPut(endpoints.role(role_id), payload),
  deleteRole: async ({ role_id }: PartialRole): Promise<PartialRole> =>
    callDelete(endpoints.role(role_id)),
};

const rolesQueryKey = (accountId: string) => [endpoints.roles, accountId];

/** Filtering first keeps a list refetch that already carries the role from duplicating it. */
const onRoleCreated = (queryClient: QueryClient, role: Role, accountId: string) => {
  queryClient.setQueryData<Roles>(rolesQueryKey(accountId), (prev) => ({
    roles: [...(prev?.roles ?? []).filter((r) => r.role_id !== role.role_id), role],
  }));
};

const onRoleUpdated = (queryClient: QueryClient, role: Role, accountId: string) => {
  queryClient.setQueryData<Roles>(rolesQueryKey(accountId), (prev) => ({
    roles: (prev?.roles ?? []).map((r) => (r.role_id === role.role_id ? role : r)),
  }));
};

const onRoleDeleted = (queryClient: QueryClient, roleId: string, accountId: string) => {
  queryClient.setQueryData<Roles>(rolesQueryKey(accountId), (prev) => ({
    roles: (prev?.roles ?? []).filter((r) => r.role_id !== roleId),
  }));
};

export const useRoles = () => {
  const accountId = useSelector(selectAccountId);
  const { t } = useTranslation();
  const queryClient = useQueryClient();

  const canRead = useSelector((state: RootState) => getPermissions(state, 'roles', actions.READ));

  const isUserOwner = useSelector(selectIsUserOwner);

  const { data: roles, status: listStatus } = useQuery<Roles, Error>({
    queryKey: rolesQueryKey(accountId),
    queryFn: () => API.listRoles(),
    enabled: !!accountId && canRead,
  });

  const cancelListRefetch = () => queryClient.cancelQueries({ queryKey: rolesQueryKey(accountId) });

  /** 409 is a name already taken, 400 a name starting with a filter operator. Neither is localised. */
  const writeErrorMessage = (error: ApiError) => {
    if (error.statusCode === 409) {
      return t('validation.name_already_exists');
    }
    if (error.statusCode === 400) {
      return t('roles.name_not_allowed');
    }
    return error.message;
  };

  const { mutate: createRole, mutateAsync: createRoleAsync } = useMutation<
    Role,
    ApiError,
    RolePayload
  >({
    mutationFn: API.createRole,
    onMutate: cancelListRefetch,
    onSuccess: (role) => {
      onRoleCreated(queryClient, role, accountId);
    },
    onError: (error) => {
      toast.error(writeErrorMessage(error));
    },
  });

  const {
    mutate: updateRole,
    status: updateStatus,
    variables: updateVariables,
  } = useMutation<Role, ApiError, RolePayload & { role_id: string }>({
    mutationFn: API.updateRole,
    onMutate: cancelListRefetch,
    onSuccess: (role) => {
      onRoleUpdated(queryClient, role, accountId);
      toast.success(t('roles.updated', { 0: role?.name }));
    },
    onError: (error) => {
      toast.error(writeErrorMessage(error));
    },
  });

  const { mutate: deleteRole, status: deleteStatus } = useMutation<
    PartialRole,
    ApiError,
    PartialRole
  >({
    mutationFn: API.deleteRole,
    onMutate: cancelListRefetch,
    onSuccess: (_resp, { role_id }) => {
      onRoleDeleted(queryClient, role_id, accountId);
      toast.success(t('roles.deleted'));
    },
    onError: (error) => {
      // The API's 409 message is not localised.
      toast.error(error.statusCode === 409 ? t('roles.delete_assigned_error') : error.message);
    },
  });

  // Show all roles except READER and WRITER
  const availableRoles: Option[] = useMemo(
    () =>
      roles?.roles?.reduce((acc, role) => {
        // Exclude READER and WRITER roles, include BILLING role if isUserOwner is true
        if (role.role_id === BILLING && !isUserOwner) {
          return acc;
        }
        if (role.role_id !== READER && role.role_id !== WRITER) {
          // System roles are localised; custom roles fall back to what the API returns.
          const copy = SYSTEM_ROLE_COPY[role.role_id];
          const newRole = {
            label: copy ? t(copy.name, role.name) : role.name,
            value: role.role_id,
            description: copy ? t(copy.description, role.description) : role.description,
            type: t('common.members_roles'),
          };
          acc.push(newRole);
        }
        return acc;
      }, []) ?? [],
    [isUserOwner, roles?.roles, t]
  );
  return {
    roles: roles?.roles ?? [],
    listStatus,
    availableRoles,
    createRole,
    createRoleAsync,
    updateRole,
    updateStatus,
    /** The role being renamed, for per-row pending state. */
    updateVariables,
    deleteRole,
    deleteStatus,
  };
};
